import React, {FC, ReactElement, useCallback, useContext, useMemo} from 'react';
import {ExportOutlined} from "@ant-design/icons";
import {Button, Dropdown} from 'antd';
import { saveAs } from 'file-saver';
import {MenuInfo} from "rc-menu/lib/interface";
import api from "../../config/axios";
import {createSearchParams, useSearchParams} from "react-router-dom";
import moment from "moment";
import {buildSearchParams, DefaultFilters} from "../../config/queryClient";
import {useFilterPrefix} from "./Filters/FilterPrefixProvider";
import {QueryFilter} from "./Filters/types";
import {OrganisationInformationContext} from "../../layout/OrganisationInformationProvider";
import {buildVisibilityFilters} from "../../config/fieldVisibility";

interface Props {
    autoExcludeRestrictedFields?: boolean,
    url: string,
    filenamePrefix?: string,
    disabled?: boolean,
    searchParamFilters?: (string | QueryFilter)[],
    defaultFilters?: DefaultFilters,
}

export const ExportButton: FC<Props> = ({
    url,
    searchParamFilters = [],
    defaultFilters = {},
    filenamePrefix = 'export',
    disabled = false,
    autoExcludeRestrictedFields = true
}): ReactElement => {
    const organisation = useContext(OrganisationInformationContext);
    const filterPrefix = useFilterPrefix();
    const [searchParams] = useSearchParams();
    const filters = useMemo(() => ([filterPrefix, ...searchParamFilters]), [searchParamFilters, filterPrefix]);
    const resolvedDefaultFilters = useMemo(() => {
        if (!autoExcludeRestrictedFields || !organisation) {
            return defaultFilters;
        }

        return buildVisibilityFilters(url, {
            canViewIndividualData: organisation.can_view_individual_data,
            canViewUserPii: organisation.can_view_user_pii
        }, defaultFilters);
    }, [autoExcludeRestrictedFields, defaultFilters, organisation, url]);

    const handleSelect = useCallback(async (item: MenuInfo) => {
        let params = buildSearchParams(searchParams, filters);
        Object.keys(resolvedDefaultFilters).forEach(key => {
            if (key in params) return;
            params[key] = resolvedDefaultFilters[key];
        });
        params = createSearchParams(params);
        let baseUrl = url;
        if (url.endsWith('/')) {
            baseUrl = url.slice(0, -1)
        }
        const response = await api.get(
            `${baseUrl}.${item.key}?${params.toString()}`,
            {responseType: 'blob'}
        );
        const filename = `${filenamePrefix}-${moment().format('MM-DD-YYYY')}.${item.key}`;
        saveAs(response.data, filename);
    }, [url, searchParams, filters, resolvedDefaultFilters, filenamePrefix]);

    return (
        <Dropdown
            menu={{
                items: [
                    {label: 'CSV', key: 'csv'},
                    {label: 'XLSX', key: 'xlsx'}
                ],
                onClick: handleSelect
            }}
            placement='bottomCenter'
            arrow
            disabled={disabled}
        >
            <Button disabled={disabled} icon={<ExportOutlined/>}>Export</Button>
        </Dropdown>
    );
};
